import { useState, useEffect, useRef } from 'react'
import { Mic, MicOff, X, CheckCircle, AlertCircle, Loader } from 'lucide-react'
import { taskAPI, calendarAPI, noteAPI, bookmarkAPI, voiceAPI } from '../services/api'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import confetti from 'canvas-confetti'

interface VoiceCommandProps {
  onClose?: () => void
}

type VoiceMethod = 'webspeech' | 'mediarecorder' | null

export default function VoiceCommand({ onClose }: VoiceCommandProps) {
  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [interimTranscript, setInterimTranscript] = useState('')
  const [voiceMethod, setVoiceMethod] = useState<VoiceMethod>(null)
  
  const recognitionRef = useRef<any>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const queryClient = useQueryClient()

  useEffect(() => {
    console.log('VoiceCommand: Initializing...')
    
    // Check if browser supports Web Speech API
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    
    if (!SpeechRecognition) {
      console.log('VoiceCommand: Web Speech API not supported, checking MediaRecorder...')
      
      // Check if MediaRecorder is supported (fallback for Firefox)
      if (typeof MediaRecorder !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        console.log('VoiceCommand: MediaRecorder API found, using backend transcription')
        setVoiceMethod('mediarecorder')
        return
      }
      
      // Neither API is supported
      console.error('VoiceCommand: No voice input method available')
      toast.error('Voice commands not supported in this browser. Try Chrome, Edge, or Safari.', { duration: 5000 })
      return
    }

    console.log('VoiceCommand: Web Speech API found, attempting to initialize...')

    // Initialize speech recognition
    try {
      const recognition = new SpeechRecognition()
      recognition.continuous = false
      recognition.interimResults = true
      recognition.lang = 'en-US'
      
      console.log('VoiceCommand: Recognition instance created')

    recognition.onstart = () => {
      setIsListening(true)
      setResult(null)
    }

    recognition.onresult = (event: any) => {
      let interim = ''
      let final = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          final += transcript
        } else {
          interim += transcript
        }
      }

      if (final) {
        setTranscript(final)
        setInterimTranscript('')
        processCommand(final)
      } else {
        setInterimTranscript(interim)
      }
    }

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error)
      setIsListening(false)
      
      if (event.error === 'not-allowed') {
        setResult({ type: 'error', message: 'Microphone access denied. Please allow microphone access.' })
      } else if (event.error === 'no-speech') {
        setResult({ type: 'error', message: 'No speech detected. Please try again.' })
      } else {
        setResult({ type: 'error', message: `Error: ${event.error}` })
      }
    }

    recognition.onend = () => {
      setIsListening(false)
    }

      recognitionRef.current = recognition
      console.log('VoiceCommand: Web Speech API initialized successfully, using native recognition')
      setVoiceMethod('webspeech')
    } catch (error) {
      console.error('VoiceCommand: Error initializing Web Speech API:', error)
      console.log('VoiceCommand: Falling back to MediaRecorder + backend transcription')
      
      // Fall back to MediaRecorder if Web Speech API fails
      if (typeof MediaRecorder !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        setVoiceMethod('mediarecorder')
        toast('Using cloud-based voice recognition', { icon: '☁️', duration: 3000 })
      } else {
        toast.error('Failed to initialize voice recognition. No fallback available.')
      }
    }

    return () => {
      if (recognitionRef.current) {
        console.log('VoiceCommand: Cleaning up recognition')
        recognitionRef.current.stop()
      }
    }
  }, [])

  // MediaRecorder-based voice input (fallback for Firefox)
  const startMediaRecorder = async () => {
    try {
      console.log('VoiceCommand: Starting MediaRecorder...')
      setTranscript('')
      setInterimTranscript('')
      setResult(null)
      setIsListening(true)
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      
      // Create MediaRecorder with appropriate MIME type
      let options: MediaRecorderOptions = { mimeType: 'audio/webm' }
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/ogg')) {
          options = { mimeType: 'audio/ogg' }
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          options = { mimeType: 'audio/mp4' }
        } else {
          options = {}
        }
      }
      
      const mediaRecorder = new MediaRecorder(stream, options)
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }
      
      mediaRecorder.onstop = async () => {
        console.log('VoiceCommand: MediaRecorder stopped, processing audio...')
        setIsListening(false)
        setIsProcessing(true)
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop())
        
        try {
          // Create audio blob
          const audioBlob = new Blob(audioChunksRef.current, { type: options.mimeType || 'audio/webm' })
          console.log('VoiceCommand: Audio blob created:', audioBlob.size, 'bytes')
          
          // Send to backend for transcription
          const formData = new FormData()
          formData.append('audio', audioBlob, 'recording.webm')
          
          console.log('VoiceCommand: Sending audio to backend...')
          const { data } = await voiceAPI.transcribe(formData)
          console.log('VoiceCommand: Transcription received:', data.text)
          
          if (data.text) {
            setTranscript(data.text)
            await processCommand(data.text)
          } else {
            setResult({ type: 'error', message: 'No speech detected. Please try again.' })
            setIsProcessing(false)
          }
        } catch (error: any) {
          console.error('VoiceCommand: Transcription error:', error)
          
          // Handle rate limiting
          if (error.response?.status === 429) {
            setResult({ 
              type: 'error', 
              message: '⏱️ Rate limit reached. Please wait a moment before trying again. (OpenAI API limit)' 
            })
          } else {
            setResult({ 
              type: 'error', 
              message: error.response?.data?.error || 'Failed to transcribe audio. Please try again.' 
            })
          }
          setIsProcessing(false)
        }
      }
      
      mediaRecorder.onerror = (event: any) => {
        console.error('VoiceCommand: MediaRecorder error:', event.error)
        setIsListening(false)
        setResult({ type: 'error', message: 'Recording error: ' + event.error })
      }
      
      mediaRecorder.start()
      console.log('VoiceCommand: MediaRecorder started')
      
      // Auto-stop after 10 seconds (max recording time)
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          console.log('VoiceCommand: Auto-stopping MediaRecorder after 10 seconds')
          mediaRecorderRef.current.stop()
        }
      }, 10000)
      
    } catch (error: any) {
      console.error('VoiceCommand: Error starting MediaRecorder:', error)
      setIsListening(false)
      if (error.name === 'NotAllowedError') {
        setResult({ type: 'error', message: 'Microphone access denied. Please allow microphone access.' })
      } else {
        setResult({ type: 'error', message: 'Failed to start recording: ' + error.message })
      }
    }
  }

  const stopMediaRecorder = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log('VoiceCommand: Stopping MediaRecorder...')
      mediaRecorderRef.current.stop()
    }
  }

  // Web Speech API-based voice input (Chrome, Edge, Safari)
  const startWebSpeech = () => {
    console.log('VoiceCommand: startWebSpeech called', {
      hasRecognition: !!recognitionRef.current,
      isListening
    })
    
    if (!recognitionRef.current) {
      console.error('VoiceCommand: recognitionRef is null! Web Speech API not initialized.')
      
      // Try to fall back to MediaRecorder if available
      if (typeof MediaRecorder !== 'undefined' && navigator.mediaDevices) {
        console.log('VoiceCommand: Falling back to MediaRecorder')
        setVoiceMethod('mediarecorder')
        toast('Switching to cloud-based voice recognition', { icon: '☁️', duration: 3000 })
        startMediaRecorder()
      } else {
        setResult({ type: 'error', message: 'Voice recognition not available. Please refresh the page.' })
      }
      return
    }
    
    if (!isListening) {
      console.log('VoiceCommand: Starting Web Speech recognition...')
      setTranscript('')
      setInterimTranscript('')
      setResult(null)
      
      try {
        recognitionRef.current.start()
        console.log('VoiceCommand: Web Speech recognition started successfully')
      } catch (error) {
        console.error('VoiceCommand: Error starting Web Speech recognition:', error)
        toast.error('Failed to start voice recognition: ' + (error as Error).message)
      }
    } else {
      console.warn('VoiceCommand: Cannot start - already listening or no recognition')
    }
  }

  const stopWebSpeech = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop()
    }
  }

  // Universal start/stop functions
  const startListening = () => {
    console.log('VoiceCommand: startListening() called with voiceMethod:', voiceMethod)
    console.log('VoiceCommand: recognitionRef.current exists:', !!recognitionRef.current)
    
    if (!voiceMethod) {
      console.error('VoiceCommand: Voice method not initialized yet')
      toast.error('Voice recognition is still initializing. Please wait a moment and try again.')
      return
    }
    
    if (voiceMethod === 'mediarecorder') {
      console.log('VoiceCommand: Using MediaRecorder (cloud transcription)')
      startMediaRecorder()
    } else {
      console.log('VoiceCommand: Using Web Speech API (native)')
      startWebSpeech()
    }
  }

  const stopListening = () => {
    if (voiceMethod === 'mediarecorder') {
      stopMediaRecorder()
    } else {
      stopWebSpeech()
    }
  }

  const processCommand = async (command: string) => {
    setIsProcessing(true)
    const lowerCommand = command.toLowerCase().trim()

    try {
      // Add task command - handle various patterns
      if (lowerCommand.includes('add task') || lowerCommand.includes('create task') || lowerCommand.includes('new task')) {
        // More flexible regex that handles commas, periods, and natural speech
        const taskMatch = lowerCommand.match(/(?:add|create|new)\s+task[,:\s]+(.+)/i)
        if (taskMatch && taskMatch[1]) {
          const taskTitle = taskMatch[1].trim().replace(/[,.]$/, '').trim() // Remove trailing punctuation
          await taskAPI.create({ title: taskTitle, status: 'pending' })
          queryClient.invalidateQueries({ queryKey: ['tasks'] })
          setResult({ type: 'success', message: `✅ Task created: "${taskTitle}"` })
          toast.success('Task created!')
          
          // 🎉 Celebrate task creation!
          confetti({
            particleCount: 50,
            spread: 60,
            origin: { y: 0.7 },
            colors: ['#0183c6', '#10b981', '#f59e0b']
          })
        } else {
          setResult({ type: 'error', message: 'Could not understand task name' })
        }
      }
      
      // Add note command
      else if (lowerCommand.includes('add note') || lowerCommand.includes('create note') || lowerCommand.includes('new note')) {
        // More flexible regex that handles commas, periods, and natural speech
        const noteMatch = lowerCommand.match(/(?:add|create|new)\s+note[,:\s]+(.+)/i)
        if (noteMatch && noteMatch[1]) {
          const noteContent = noteMatch[1].trim().replace(/[,.]$/, '').trim()
          await noteAPI.create({ title: 'Voice Note', content: noteContent })
          queryClient.invalidateQueries({ queryKey: ['notes'] })
          setResult({ type: 'success', message: `📝 Note created: "${noteContent.substring(0, 50)}..."` })
          toast.success('Note created!')
        } else {
          setResult({ type: 'error', message: 'Could not understand note content' })
        }
      }
      
      // Add bookmark command
      else if (lowerCommand.includes('add bookmark') || lowerCommand.includes('create bookmark') || lowerCommand.includes('save bookmark')) {
        // More flexible regex that handles commas, periods, and natural speech
        const bookmarkMatch = lowerCommand.match(/(?:add|create|save)\s+bookmark[,:\s]+(.+)/i)
        if (bookmarkMatch && bookmarkMatch[1]) {
          const bookmarkUrl = bookmarkMatch[1].trim().replace(/[,.]$/, '').trim()
          // Simple URL validation
          let url = bookmarkUrl
          if (!url.startsWith('http')) {
            url = 'https://' + url
          }
          await bookmarkAPI.create({ url, title: url, folder: 'Voice Bookmarks' })
          queryClient.invalidateQueries({ queryKey: ['bookmarks'] })
          setResult({ type: 'success', message: `🔖 Bookmark saved: "${url}"` })
          toast.success('Bookmark saved!')
        } else {
          setResult({ type: 'error', message: 'Could not understand bookmark URL' })
        }
      }
      
      // Add event/calendar command
      else if (lowerCommand.includes('event') || lowerCommand.includes('schedule') || lowerCommand.includes('calendar')) {
        let title = ''
        let dateStr = ''
        
        // Pattern 1: "create event [title] on [date]"
        let eventMatch = lowerCommand.match(/(?:add|create|schedule|new)(?:\s+(?:a|an|event|calendar event|new))?\s+(.+?)\s+(?:on|at|for)\s+(.+)/i)
        if (eventMatch && eventMatch[1] && eventMatch[2]) {
          title = eventMatch[1].trim()
          dateStr = eventMatch[2].trim()
        }
        
        // Pattern 2: "create event for [date] to [title]" (date-first)
        if (!title || !dateStr) {
          eventMatch = lowerCommand.match(/(?:add|create|schedule|new)(?:\s+(?:a|an|event|calendar event|new))?\s+(?:for|on)\s+(.+?)\s+(?:to|for)\s+(.+)/i)
          if (eventMatch && eventMatch[1] && eventMatch[2]) {
            dateStr = eventMatch[1].trim()
            title = eventMatch[2].trim()
          }
        }
        
        if (title && dateStr) {
          // Parse date (basic parsing)
          const date = parseDate(dateStr)
          if (date) {
            try {
              await calendarAPI.createEvent({
                title,
                start_time: date.toISOString(),
                end_time: new Date(date.getTime() + 60 * 60 * 1000).toISOString(), // 1 hour duration
                all_day: false,
              })
              queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
              setResult({ type: 'success', message: `📅 Event created: "${title}" on ${date.toLocaleDateString()}` })
              toast.success('Event created!')
              
              // 🎉 Celebrate event creation!
              confetti({
                particleCount: 60,
                spread: 70,
                origin: { y: 0.7 },
                colors: ['#0183c6', '#8b5cf6', '#ec4899']
              })
            } catch (error: any) {
              console.error('Failed to create event:', error)
              setResult({ type: 'error', message: `Failed to create event: ${error.response?.data?.error || error.message}` })
              toast.error('Failed to create event')
            }
          } else {
            setResult({ type: 'error', message: `Could not parse date: "${dateStr}". Try "tomorrow", "October 15th", or "next Monday"` })
          }
        } else {
          setResult({ type: 'error', message: 'Could not understand event details. Try "Create event [title] on [date]" or "Create event for [date] to [action]"' })
        }
      }
      
      // What's the weather
      else if (lowerCommand.includes('weather') || lowerCommand.includes("what's the weather")) {
        setResult({ type: 'success', message: '🌤️ Check your Weather widget for current conditions!' })
        toast('Check the Weather widget', { icon: '🌤️' })
      }
      
      // Help command
      else if (lowerCommand.includes('help') || lowerCommand === 'what can you do') {
        setResult({ 
          type: 'success', 
          message: `Available commands:
• "Add task [name]"
• "Add note [content]"
• "Add bookmark [url]"
• "Create event [title] on [date]"
• "Create event for [date] to [action]"
• "What's the weather?"

Date formats: today, tomorrow, October 15th, next Monday, 10/15` 
        })
      }
      
      // Unknown command
      else {
        setResult({ 
          type: 'error', 
          message: 'Command not recognized. Say "help" to see available commands.' 
        })
      }
    } catch (error: any) {
      console.error('Command processing error:', error)
      setResult({ type: 'error', message: 'Failed to process command: ' + (error.message || 'Unknown error') })
      toast.error('Failed to process command')
    } finally {
      setIsProcessing(false)
    }
  }

  // Simple date parser for voice commands
  const parseDate = (dateStr: string): Date | null => {
    const now = new Date()
    const lower = dateStr.toLowerCase()

    // Today
    if (lower.includes('today')) {
      const time = extractTime(lower)
      const date = new Date(now)
      if (time) {
        date.setHours(time.hours, time.minutes, 0, 0)
      }
      return date
    }

    // Tomorrow
    if (lower.includes('tomorrow')) {
      const time = extractTime(lower)
      const date = new Date(now)
      date.setDate(date.getDate() + 1)
      if (time) {
        date.setHours(time.hours, time.minutes, 0, 0)
      }
      return date
    }

    // Next week
    if (lower.includes('next week')) {
      const date = new Date(now)
      date.setDate(date.getDate() + 7)
      return date
    }

    // Next [day of week] or just [day of week]
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    for (let i = 0; i < days.length; i++) {
      // Check for "next [day]"
      if (lower.includes('next ' + days[i])) {
        const date = new Date(now)
        const currentDay = date.getDay()
        const targetDay = i
        let daysToAdd = targetDay - currentDay
        if (daysToAdd <= 0) daysToAdd += 7
        date.setDate(date.getDate() + daysToAdd)
        const time = extractTime(lower)
        if (time) {
          date.setHours(time.hours, time.minutes, 0, 0)
        }
        return date
      }
      // Check for standalone day name (e.g., "wednesday", "friday")
      // Use word boundaries to avoid partial matches
      const dayRegex = new RegExp(`\\b${days[i]}\\b`, 'i')
      if (dayRegex.test(lower)) {
        const date = new Date(now)
        const currentDay = date.getDay()
        const targetDay = i
        let daysToAdd = targetDay - currentDay
        // If it's today or has passed this week, go to next week
        if (daysToAdd <= 0) daysToAdd += 7
        date.setDate(date.getDate() + daysToAdd)
        const time = extractTime(lower)
        if (time) {
          date.setHours(time.hours, time.minutes, 0, 0)
        }
        return date
      }
    }

    // Month + Day (e.g., "October 15", "October 15th", "oct 15")
    const months = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ]
    const monthsShort = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
    
    for (let i = 0; i < months.length; i++) {
      const monthMatch = lower.match(new RegExp(`(${months[i]}|${monthsShort[i]})\\s+(\\d{1,2})(?:st|nd|rd|th)?`, 'i'))
      if (monthMatch) {
        const day = parseInt(monthMatch[2])
        const date = new Date(now.getFullYear(), i, day)
        
        // If the date has already passed this year, use next year
        if (date < now) {
          date.setFullYear(now.getFullYear() + 1)
        }
        
        const time = extractTime(lower)
        if (time) {
          date.setHours(time.hours, time.minutes, 0, 0)
        } else {
          date.setHours(12, 0, 0, 0) // Default to noon if no time specified
        }
        
        return date
      }
    }

    // Numeric date formats (e.g., "10/15", "10-15", "10.15")
    const numericMatch = lower.match(/(\d{1,2})[\/\-\.](\d{1,2})/)
    if (numericMatch) {
      const month = parseInt(numericMatch[1]) - 1 // JavaScript months are 0-indexed
      const day = parseInt(numericMatch[2])
      const date = new Date(now.getFullYear(), month, day)
      
      // If the date has already passed this year, use next year
      if (date < now) {
        date.setFullYear(now.getFullYear() + 1)
      }
      
      const time = extractTime(lower)
      if (time) {
        date.setHours(time.hours, time.minutes, 0, 0)
      } else {
        date.setHours(12, 0, 0, 0)
      }
      
      return date
    }

    return null
  }

  // Extract time from string
  const extractTime = (str: string): { hours: number; minutes: number } | null => {
    // Match patterns like "3pm", "3:30pm", "3 p.m.", "3:30 a.m.", "15:00", etc.
    const timeMatch = str.match(/(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?/i)
    if (timeMatch) {
      let hours = parseInt(timeMatch[1])
      const minutes = timeMatch[2] ? parseInt(timeMatch[2]) : 0
      const period = timeMatch[3]?.toLowerCase()

      if (period === 'p' && hours < 12) hours += 12
      if (period === 'a' && hours === 12) hours = 0

      return { hours, minutes }
    }
    return null
  }

  console.log('VoiceCommand: Rendering', { transcript, isProcessing, result: !!result, isListening })

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Main Voice Button */}
      {!transcript && !isProcessing && !result && voiceMethod && (
        <button
          onClick={(e) => {
            console.log('VoiceCommand: Button clicked!')
            e.preventDefault()
            e.stopPropagation()
            if (isListening) {
              stopListening()
            } else {
              startListening()
            }
          }}
          className={`group relative flex items-center justify-center w-16 h-16 rounded-full shadow-lg transition-all duration-300 ${
            isListening
              ? 'bg-red-500 hover:bg-red-600 scale-110 animate-pulse'
              : 'hover:scale-110'
          }`}
          style={{
            backgroundColor: isListening ? undefined : 'var(--color-button-bg, #0183c6)',
          }}
          title={isListening ? 'Stop listening' : 'Start voice command'}
        >
          {isListening ? (
            <MicOff size={28} className="text-white" />
          ) : (
            <Mic size={28} className="text-white" />
          )}
          
          {/* Ripple effect when listening */}
          {isListening && (
            <>
              <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-75"></span>
              <span className="absolute inset-0 rounded-full bg-red-400 animate-pulse opacity-50"></span>
            </>
          )}
        </button>
      )}

      {/* Interim transcript (while speaking) */}
      {isListening && interimTranscript && (
        <div className="absolute bottom-20 right-0 bg-white rounded-lg shadow-xl p-4 max-w-2xl border-2 border-primary-500">
          <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
            <Loader className="animate-spin" size={16} />
            <span>Listening...</span>
          </div>
          <p className="text-gray-800 italic">{interimTranscript}</p>
        </div>
      )}

      {/* Processing state */}
      {isProcessing && transcript && (
        <div className="absolute bottom-20 right-0 bg-white rounded-lg shadow-xl p-4 max-w-2xl border-2 border-blue-500">
          <div className="flex items-center gap-2 text-blue-600 text-sm mb-2">
            <Loader className="animate-spin" size={16} />
            <span>Processing command...</span>
          </div>
          <p className="text-gray-800 font-medium">{transcript}</p>
        </div>
      )}

      {/* Result display */}
      {result && transcript && (
        <div className={`absolute bottom-20 right-0 rounded-lg shadow-xl p-4 max-w-2xl border-2 ${
          result.type === 'success' 
            ? 'bg-green-50 border-green-500' 
            : 'bg-red-50 border-red-500'
        }`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {result.type === 'success' ? (
                  <CheckCircle className="text-green-600" size={20} />
                ) : (
                  <AlertCircle className="text-red-600" size={20} />
                )}
                <span className={`font-semibold ${
                  result.type === 'success' ? 'text-green-700' : 'text-red-700'
                }`}>
                  {result.type === 'success' ? 'Success!' : 'Error'}
                </span>
              </div>
              
              <p className="text-gray-700 text-sm mb-2">
                <strong>You said:</strong> "{transcript}"
              </p>
              
              <p className={`text-sm whitespace-pre-line ${
                result.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {result.message}
              </p>
            </div>
            
            <button
              onClick={() => {
                setTranscript('')
                setResult(null)
              }}
              className="flex-shrink-0 p-1 hover:bg-gray-200 rounded"
            >
              <X size={16} />
            </button>
          </div>
          
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => {
                setTranscript('')
                setResult(null)
                startListening()
              }}
              className="flex-1 px-3 py-1.5 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 transition"
            >
              Try Again
            </button>
            <button
              onClick={() => {
                setTranscript('')
                setResult(null)
              }}
              className="px-3 py-1.5 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 transition"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

